'use client';

import { useFormContext } from 'react-hook-form';

import { Button, TextButton } from '@flipster/react/design-system';
import { css } from '@flipster/styled-system/css';
import { flex } from '@flipster/styled-system/patterns';
import { FormItem, PasswordInput, SessionInput } from '@flipster/ui';

import { ContinueWithApple } from '@web/app/[locale]/(web)/_auth/continue-with-apple';
import { ContinueWithGoogle } from '@web/app/[locale]/(web)/_auth/continue-with-google';
import { ContinueWithPasskey } from '@web/app/[locale]/(web)/_auth/continue-with-passkey';
import { useBlockedRegion } from '@web/hooks/useBlockedRegion';
import { LoginSchema } from '@web/utils/session-validator';

import { restrictedMessage } from '../_constants';
import { divider, formItem, h1, header, socialButtonGroup, wrapper } from '../_style';

interface Props {
  loading: boolean;
  disabled: boolean;
  onPasskeyButtonClick?: () => void;
}

const LoginForm = ({ disabled, loading, onPasskeyButtonClick }: Props) => {
  const { isRestrictedRegion } = useBlockedRegion();
  const {
    formState: { errors },
  } = useFormContext<LoginSchema>();

  return (
    <div className={wrapper}>
      <header className={header}>
        <h1 className={h1}>Log In</h1>
      </header>

      <FormItem name='email' label='E-mail' className={formItem} error={errors.email?.message}>
        <SessionInput placeholder='name@flipster.io' />
      </FormItem>
      <FormItem name='password' label='Password' className={formItem} error={errors.password?.message}>
        <PasswordInput placeholder='Password' />
      </FormItem>
      {isRestrictedRegion && (
        <p className={css({ textStyle: 'Text/S/Regular', pb: 6, color: 'content.default.level-4' })}>
          {restrictedMessage}
        </p>
      )}

      <div className={flex({ mt: 24, justify: 'center' })}>
        <TextButton color='accent' size='large' as='a' href='/forgotPassword' label='Forgot your password?' />
      </div>
      <Button
        type='submit'
        isLoading={loading}
        disabled={disabled}
        label='Log In'
        size='large'
        css={{ w: '100%', mt: 32 }}
      />
      <div className={divider}>OR</div>
      <div className={socialButtonGroup}>
        {onPasskeyButtonClick && (
          <ContinueWithPasskey label='Log in with Passkey' onClick={onPasskeyButtonClick} disabled={loading} />
        )}
        <ContinueWithGoogle label='Log in with Google' disabled={loading} />
        <ContinueWithApple label='Log in with Apple' disabled={loading} />
      </div>
    </div>
  );
};

export default LoginForm;
